// Sidebar controls — separator, search input, content inset, and the
// expand/collapse trigger button. Trigger is the only one with a
// behavior (toggleSidebar from useSidebar); the rest are styled
// passthroughs.

import * as React from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { PanelLeft } from 'lucide-react'
import { cn } from '../../../../src/cn'
import { useSidebar } from './use-sidebar'

export const SidebarSeparator: React.ForwardRefExoticComponent<
  React.PropsWithoutRef<React.HTMLAttributes<HTMLDivElement>> & React.RefAttributes<HTMLDivElement>
> = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <Separator ref={ref} className={cn('mx-2 w-auto bg-sidebar-border', className)} {...props} />
  ),
)
SidebarSeparator.displayName = 'SidebarSeparator'

export const SidebarInput = React.forwardRef<HTMLInputElement, React.ComponentProps<'input'>>(
  ({ className, ...props }, ref) => (
    <Input
      ref={ref}
      className={cn(
        'h-8 w-full bg-background shadow-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
        className,
      )}
      {...props}
    />
  ),
)
SidebarInput.displayName = 'SidebarInput'

export const SidebarInset = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-1 flex-col bg-background', className)} {...props} />
  ),
)
SidebarInset.displayName = 'SidebarInset'

export const SidebarTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button>
>(({ className, onClick, ...props }, ref) => {
  const { toggleSidebar } = useSidebar()
  return (
    <Button
      ref={ref}
      data-sidebar="trigger"
      variant="ghost"
      size="icon"
      aria-label="Toggle sidebar navigation"
      aria-expanded={props['aria-expanded'] ?? undefined}
      className={cn('h-7 w-7 focus-visible:ring-2 focus-visible:ring-offset-2', className)}
      onClick={(event) => {
        onClick?.(event)
        toggleSidebar()
      }}
      {...props}
    >
      <PanelLeft className="h-4 w-4" />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  )
})
SidebarTrigger.displayName = 'SidebarTrigger'
